import { clone, merge } from 'lodash';
import { Vector3 } from 'three';
import { IMachineState } from '../interfaces/i-machine-state';
import {
  IBallState,
  IPlateLoc,
  ITrajectory,
  ITrajektRefBreak,
} from '../interfaces/pitches';
import {
  IAcceleration,
  IAziAltCoordinate,
  IPosition,
  IVelocity,
} from '../interfaces/pitches/i-base';
import { IMachineShot } from '../interfaces/training/i-machine-shot';
import { IMSBSShot } from '../interfaces/training/i-msbs-shot';
import { IRapsodoBreak } from '../interfaces/training/i-rapsodo-shot';
import { BASEBALL_DRAG_COEFF } from './ball.helper';
import { BreakHelper } from './break.helper';
import {
  BALL_RADIUS_FT,
  FTPS_TO_MPH,
  FT_TO_INCHES,
  GRAVITY_FT_PER_SEC,
  METERS_TO_INCHES,
  MPH_TO_FTPS,
  RPM_TO_RAD_PER_SEC,
  getMagnitude,
  getRotationMatrix,
} from './math.utilities';
import { PLATE_EDGE_FT, PLATE_MIDDLE_FT } from './plate.helper';

// 1/16th of inch => ft
const MAX_ROTATION_ERROR_FT = 1 / 16 / FT_TO_INCHES;

/** Approx 1 [degree per foot] (gt 1/2x, lt 2x, in [rad / foot]) */
const AZIMUTH_COEFF = 0.01;

/** Approx 1 [degree per foot] (gt 1/2x, lt 2x, in [rad / foot]) */
const ALTITUDE_COEFF = 0.01;

/** hard cap so a non-converging aim (e.g. a trajectory with a nonsensical
 * acceleration) can't loop forever and block the event loop */
const MAX_AIM_ITERATIONS = 10_000;

export class TrajHelper {
  // result is in MPH, traj has velos in FTPS
  static getSpeedFromTraj(value: ITrajectory): number {
    try {
      return getMagnitude([value.vx, value.vy, value.vz]) * FTPS_TO_MPH;
    } catch (e) {
      console.error('getSpeedFromTraj', value, e);
      return 0;
    }
  }

  /** Provide PLATE_EDGE_FT as endY for break calculations */
  static getFlightTimeSec(value: ITrajectory, endY = PLATE_MIDDLE_FT): number {
    try {
      // don't bother if traj says pitch is moving in the wrong (or no) direction
      if (value.vy === 0) {
        throw new Error('cannot solve when vy is zero');
      }

      if (Math.abs(value.ay) < 1e-6) {
        return (endY - value.py) / value.vy;
      }

      // solve for roots using binomial theorem
      const a = value.ay / 2;
      const b = value.vy;
      const c = value.py - endY;

      const discriminant = Math.pow(b, 2) - 4 * a * c;

      if (discriminant <= 0) {
        throw new Error(
          `cannot solve when discriminant is non-positive (${discriminant})`
        );
      }

      const output = (-b - Math.sqrt(discriminant)) / (2 * a);
      return output;
    } catch (e) {
      console.error(e);
      return NaN;
    }
  }

  /** Provide PLATE_EDGE_FT as endY for break calculations */
  static getPlateLoc(value: ITrajectory, endY = PLATE_MIDDLE_FT): IPlateLoc {
    try {
      const timeSec = this.getFlightTimeSec(value, endY);

      if (isNaN(timeSec)) {
        throw new Error('timeSec is NaN');
      }

      return {
        plate_x:
          0.5 * value.ax * Math.pow(timeSec, 2) + value.vx * timeSec + value.px,
        plate_z:
          0.5 * value.az * Math.pow(timeSec, 2) + value.vz * timeSec + value.pz,
      };
    } catch (e) {
      console.error(e);

      return {
        plate_x: NaN,
        plate_z: NaN,
      };
    }
  }

  // in trajekt frame of ref
  static getBreaks(value: ITrajectory): ITrajektRefBreak {
    try {
      // we use still use front of home plate for break calculations
      const endY = PLATE_EDGE_FT;

      // time of flight, in seconds
      const time = this.getFlightTimeSec(value, endY);

      if (isNaN(time)) {
        throw new Error('timeSec is NaN');
      }

      const finalPlate = this.getPlateLoc(value, endY);

      const breaks = BreakHelper.getBreaks({
        initial: {
          pos: {
            px: value.px,
            py: value.py,
            pz: value.pz,
          },
          velo: {
            vx: value.vx,
            vy: value.vy,
            vz: value.vz,
          },
        },
        final: {
          pos: {
            px: finalPlate.plate_x,
            py: endY,
            pz: finalPlate.plate_z,
          },
          time: time,
        },
      });

      return {
        xInches: breaks.xInches,
        zInches: breaks.zInches,
      };
    } catch (e) {
      console.error(e);

      return {
        zInches: NaN,
        xInches: NaN,
      };
    }
  }

  // in trajekt frame of ref
  static getBreaksFromShot(value: IMachineShot): ITrajektRefBreak {
    if (value.break) {
      return {
        xInches: -1 * value.break.PITCH_HBTrajectory * METERS_TO_INCHES,
        zInches: value.break.PITCH_VBTrajectory * METERS_TO_INCHES,
      };
    }

    // shouldn't trigger as long as all shots have break (true after 2023-08-31 in prod)
    return this.getBreaks(value.traj);
  }

  static getRapsodoBreaks(value: ITrajectory): IRapsodoBreak | undefined {
    try {
      // Horizontral breaks are negative of X movement
      const trajBreaks = this.getBreaks(value);

      const output: IRapsodoBreak = {
        PITCH_HBSpin: -trajBreaks.xInches / METERS_TO_INCHES,
        PITCH_VBSpin: trajBreaks.zInches / METERS_TO_INCHES,
        PITCH_HBTrajectory: -trajBreaks.xInches / METERS_TO_INCHES,
        PITCH_VBTrajectory: trajBreaks.zInches / METERS_TO_INCHES,
        PITCH_HBSpinConfidence: 1,
        PITCH_VBSpinConfidence: 1,
        PITCH_HBTrajectoryConfidence: 1,
        PITCH_VBTrajectoryConfidence: 1,
      };

      return output;
    } catch (e) {
      console.error(e);
      return undefined;
    }
  }

  static getVisionBreaks(value: IMSBSShot): IRapsodoBreak | undefined {
    try {
      const traj = value.Traj;

      if (!traj) {
        throw new Error('getVisionBreaks failed (no Traj)');
      }

      const confidence = value.analysis_data?.confidence_metrics?.confidence;

      // Horizontral breaks are negative of X movement
      const trajBreaks = this.getBreaks(traj);

      const output: IRapsodoBreak = {
        PITCH_HBSpin: -trajBreaks.xInches / METERS_TO_INCHES,
        PITCH_VBSpin: trajBreaks.zInches / METERS_TO_INCHES,
        PITCH_HBTrajectory: -trajBreaks.xInches / METERS_TO_INCHES,
        PITCH_VBTrajectory: trajBreaks.zInches / METERS_TO_INCHES,
        PITCH_HBSpinConfidence: confidence?.break_x ?? 0,
        PITCH_VBSpinConfidence: confidence?.break_z ?? 0,
        PITCH_HBTrajectoryConfidence: confidence?.break_x ?? 0,
        PITCH_VBTrajectoryConfidence: confidence?.break_z ?? 0,
      };

      return output;
    } catch (e) {
      console.error(e);
      return undefined;
    }
  }

  private static getTimeToPy(value: ITrajectory, new_py: number): number {
    // TODO: Assert that vy < -50, ay is positive, etc.
    if (Math.abs(value.ay) < 1e-5) {
      return (new_py - value.py) / value.vy;
    }
    return (
      (-value.vy -
        Math.sqrt(Math.pow(value.vy, 2) - 2 * value.ay * (value.py - new_py))) /
      value.ay
    );
  }

  static translateTraj(input: ITrajectory, position: IPosition): ITrajectory {
    return {
      ...input,
      px: position.px,
      pz: position.pz,
      py: position.py,
    };
  }

  static extrapolateTrajectory(value: ITrajectory, py: number): ITrajectory {
    const delta = this.getTimeToPy(value, py);

    const output: ITrajectory = {
      ...value,
    };

    output.px = output.px + output.vx * delta + (output.ax * delta * delta) / 2;
    output.py = output.py + output.vy * delta + (output.ay * delta * delta) / 2;
    output.pz = output.pz + output.vz * delta + (output.az * delta * delta) / 2;
    output.vx = output.vx + output.ax * delta;
    output.vy = output.vy + output.ay * delta;
    output.vz = output.vz + output.az * delta;

    if (Math.abs(output.py - py) > 0.1) {
      console.warn({
        event: `extrapolateTrajectory to ${py} failed`,
        input: value,
        output: output,
      });
    }

    return output;
  }

  static rotateTraj(
    value: ITrajectory,
    rotation: IAziAltCoordinate
  ): ITrajectory {
    try {
      const mx = getRotationMatrix({
        x_rad: -rotation.altitude_rad,
        z_rad: rotation.azimuth_rad,
      });

      /** original velocity */
      const vVector = new Vector3(value.vx, value.vy, value.vz);

      /** rotated velocity */
      const vPrime = vVector.applyMatrix3(mx);

      /** original acceleration, gravity included */
      const aVector = new Vector3(
        value.ax,
        value.ay,
        value.az + GRAVITY_FT_PER_SEC
      );

      /** rotated acceleration, gravity included */
      const aPrime = aVector.applyMatrix3(mx);

      const output: ITrajectory = clone(value);

      const source: Partial<ITrajectory> = {
        vx: vPrime.x,
        vy: vPrime.y,
        vz: vPrime.z,
        ax: aPrime.x,
        ay: aPrime.y,
        az: aPrime.z - GRAVITY_FT_PER_SEC,
      };

      merge(output, source);

      return output;
    } catch (e) {
      console.error(e);
      return clone(value);
    }
  }

  static getAltAzForTranslation(
    traj: ITrajectory,
    release: IPosition,
    plate: IPlateLoc
  ): IAziAltCoordinate {
    const safeTraj: ITrajectory = {
      ...traj,
      px: release.px,
      py: release.py,
      pz: release.pz,
    };

    // will be modified in loop
    const output: IAziAltCoordinate = { altitude_rad: 0, azimuth_rad: 0 };

    // working, will be modified in loop
    const wPlate = this.getPlateLoc(safeTraj);

    let iterations = 0;

    while (
      Math.abs(wPlate.plate_x - plate.plate_x) > MAX_ROTATION_ERROR_FT ||
      Math.abs(wPlate.plate_z - plate.plate_z) > MAX_ROTATION_ERROR_FT
    ) {
      if (iterations++ >= MAX_AIM_ITERATIONS) {
        console.error('getAltAzForTranslation failed to converge; aborting', {
          traj: safeTraj,
          plate,
          wPlate,
          output,
        });
        break;
      }

      output.azimuth_rad += (plate.plate_x - wPlate.plate_x) * AZIMUTH_COEFF;
      output.altitude_rad += (plate.plate_z - wPlate.plate_z) * ALTITUDE_COEFF;

      const rTraj = this.rotateTraj(safeTraj, output);
      const rPlate = this.getPlateLoc(rTraj);

      wPlate.plate_x = rPlate.plate_x;
      wPlate.plate_z = rPlate.plate_z;
    }

    return output;
  }

  static aimTrajectory(
    traj: ITrajectory,
    release: IPosition,
    plate: IPlateLoc
  ): ITrajectory {
    return this.rotateTraj(
      traj,
      this.getAltAzForTranslation(traj, release, plate)
    );
  }

  static calc_tr = (config: {
    ay: number;
    vy: number;
    py: number;
    new_py: number;
  }) => {
    // TODO: Assert that vy < -50, ay is positive, etc.
    if (Math.abs(config.ay) < 1e-5) {
      return (config.new_py - config.py) / config.vy;
    }
    return (
      (-config.vy -
        Math.sqrt(
          Math.pow(config.vy, 2) - 2 * config.ay * (config.py - config.new_py)
        )) /
      config.ay
    );
  };

  // estimate traj from bs + plate w/o building anything
  static getSpinsTraj(bs: IBallState, plate: IPlateLoc): ITrajectory {
    // traj shares release position of bs
    const releasePos: IPosition = {
      px: bs.px,
      py: bs.py,
      pz: bs.pz,
    };

    // convert from bs vnet (mph) to traj vnet (ftps)
    const vnet = bs.vnet * MPH_TO_FTPS;

    // approximate acceleration values
    const ay = BASEBALL_DRAG_COEFF * Math.pow(vnet, 2);
    const tf = this.calc_tr({
      ay: ay,
      vy: -1 * vnet,
      py: bs.py,
      new_py: PLATE_EDGE_FT,
    });
    const sRX = (Math.abs(bs.wx * RPM_TO_RAD_PER_SEC) * BALL_RADIUS_FT) / vnet;
    const sRZ = (Math.abs(bs.wz * RPM_TO_RAD_PER_SEC) * BALL_RADIUS_FT) / vnet;
    const breakX = (Math.sign(bs.wz) * 10) / (1.25 + 0.75 / sRZ);
    const breakZInduced = (-Math.sign(bs.wx) * 10) / (1.25 + 0.75 / sRX);
    const ax = (2 * breakX) / Math.pow(tf, 2);
    const az = (2 * breakZInduced) / Math.pow(tf, 2) - GRAVITY_FT_PER_SEC;

    // this traj has 100% velo in y direction
    const traj: ITrajectory = {
      px: releasePos.px,
      py: releasePos.py,
      pz: releasePos.pz,
      vx: 0,
      vy: -vnet,
      vz: 0,
      ax: ax,
      ay: ay,
      az: az,
      wnet: bs.wnet,
    };

    // point the traj at the provided plate location
    const output = this.aimTrajectory(traj, releasePos, plate);
    return output;
  }

  // for break priority where wx and wz are not provided
  static getBreaksTraj(config: {
    releasePos: IPosition;
    breaksFront: ITrajektRefBreak; // breaks at front of plate
    plateMid: IPlateLoc; // plate location at midpoint of plate
    vnetFTPS: number;
    wnetRPM?: number;
    verbose?: boolean;
  }): ITrajectory {
    const MAX_ITERATIONS = 20;
    const MAX_DELTA_TOLERANCE = 1e-8;

    // get correct vx and vz without changing break or plate
    const getVxVz = (config: {
      breaksFront: ITrajektRefBreak; // todo: this is not necessary
      releasePos: IPosition; // todo: this is not necessary
      plateMid: IPlateLoc; // todo: this is not necessary
      vy: number;
      tFrontSec: number;
      tMidSec: number;
    }): { vx: number; vz: number } => {
      try {
        // aka: epsilon value
        const MAX_ERROR = 1e-8;

        const { breaksFront, releasePos, plateMid, vy, tFrontSec, tMidSec } =
          config;

        const breakXFt = breaksFront.xInches / FT_TO_INCHES;
        const breakZFt = breaksFront.zInches / FT_TO_INCHES;
        const dxMid = plateMid.plate_x - releasePos.px;
        const dzMid = plateMid.plate_z - releasePos.pz;
        const dyFront = releasePos.py - PLATE_EDGE_FT;
        const r = tFrontSec / tMidSec;
        const rSq = r * r;
        const k = tFrontSec - tMidSec * rSq;
        const denomX = k + dyFront / vy;

        if (Math.abs(denomX) < MAX_ERROR) {
          throw new Error('Unstable vx correction denominator.');
        }

        const vx = (breakXFt - dxMid * rSq) / denomX;
        const vxy = Math.sqrt(vx * vx + vy * vy);
        const denomZ = k - dyFront / vxy;

        if (Math.abs(denomZ) < MAX_ERROR) {
          throw new Error('Unstable vz correction denominator.');
        }

        const vz =
          (breakZFt -
            dzMid * rSq -
            0.5 * GRAVITY_FT_PER_SEC * tFrontSec * tFrontSec) /
          denomZ;
        return { vx, vz };
      } catch (e) {
        console.error(e);
        return { vx: 0, vz: 0 };
      }
    };

    const { releasePos, breaksFront, plateMid, vnetFTPS, wnetRPM = 0 } = config;

    const vnetFtpsSq = Math.pow(vnetFTPS, 2);

    let vy = -vnetFTPS;
    let converged = false;

    // todo: encapsulate this in a pure fn
    for (let iter = 0; iter < MAX_ITERATIONS; iter++) {
      const ay = BASEBALL_DRAG_COEFF * Math.pow(vy, 2);
      const tFrontSec = this.calc_tr({
        ay: ay,
        vy: vy,
        py: releasePos.py,
        new_py: PLATE_EDGE_FT,
      });
      const tMidSec = this.calc_tr({
        ay: ay,
        vy: vy,
        py: releasePos.py,
        new_py: PLATE_MIDDLE_FT,
      });
      const { vx, vz } = getVxVz({
        breaksFront,
        releasePos,
        plateMid,
        vy,
        tFrontSec,
        tMidSec,
      });
      const xzSpeedSq = Math.pow(vx, 2) + Math.pow(vz, 2);

      if (xzSpeedSq >= vnetFtpsSq) {
        // shouldn't trigger
        console.error('Cannot preserve vnet: solved vx/vz exceed total speed.');
        break;
      }

      const vyNext = -Math.sqrt(vnetFtpsSq - xzSpeedSq);

      if (Math.abs(vyNext - vy) < MAX_DELTA_TOLERANCE) {
        vy = vyNext;
        converged = true;
        break;
      }
      vy = vyNext;
    }

    if (!converged) {
      // we failed to converge so use the initial guess
      vy = -vnetFTPS;
    }

    // calculate acceleration using solved vy
    const ay = BASEBALL_DRAG_COEFF * Math.pow(vy, 2);
    const tFrontSec = this.calc_tr({
      ay: ay,
      vy: vy,
      py: releasePos.py,
      new_py: PLATE_EDGE_FT,
    });
    const tMidSec = this.calc_tr({
      ay: ay,
      vy: vy,
      py: releasePos.py,
      new_py: PLATE_MIDDLE_FT,
    });
    const { vx, vz } = getVxVz({
      breaksFront,
      releasePos,
      plateMid,
      vy,
      tFrontSec,
      tMidSec,
    });
    const tMidSq = tMidSec * tMidSec;
    const ax = (2 * (plateMid.plate_x - releasePos.px - vx * tMidSec)) / tMidSq;
    const az = (2 * (plateMid.plate_z - releasePos.pz - vz * tMidSec)) / tMidSq;

    // generate the output already pointing at the given plate
    const output: ITrajectory = {
      px: releasePos.px,
      py: releasePos.py,
      pz: releasePos.pz,
      vx: vx,
      vy: vy,
      vz: vz,
      ax: ax,
      ay: ay,
      az: az,
      wnet: wnetRPM,
    };

    const outPlate = this.getPlateLoc(output);

    // if the output is too far from the input, aim it
    if (
      Math.max(
        Math.abs(outPlate.plate_x - config.plateMid.plate_x),
        Math.abs(outPlate.plate_z - config.plateMid.plate_z)
      ) > 1e-3
    ) {
      // shouldn't trigger
      const aimed = this.aimTrajectory(output, releasePos, {
        plate_x: config.plateMid.plate_x,
        plate_z: config.plateMid.plate_z,
      });

      return aimed;
    }

    // output is close enough to target
    return output;
  }

  static getTrajFromBallPathEndPoints(config: {
    startPosition: IPosition;
    endPosition: IPosition;
    startVelocity: IVelocity;
    timeDelta: number;
    wnet: number;
  }): ITrajectory {
    // * constructs a trajectory from start and end points, start velocity, and time between them
    // * positions should be in feet, velocity in ft/s and time in seconds
    const { startPosition, endPosition, startVelocity, timeDelta, wnet } =
      config;
    const timeSqaured = Math.pow(timeDelta, 2);
    const accel: IAcceleration = {
      ax:
        (2 *
          (endPosition.px - startPosition.px - startVelocity.vx * timeDelta)) /
        timeSqaured,
      ay:
        (2 *
          (endPosition.py - startPosition.py - startVelocity.vy * timeDelta)) /
        timeSqaured,
      az:
        (2 *
          (endPosition.pz - startPosition.pz - startVelocity.vz * timeDelta)) /
        timeSqaured,
    };
    const output: ITrajectory = {
      px: startPosition.px,
      py: startPosition.py,
      pz: startPosition.pz,
      vx: startVelocity.vx,
      vy: startVelocity.vy,
      vz: startVelocity.vz,
      ax: accel.ax,
      ay: accel.ay,
      az: accel.az,
      wnet: wnet,
    };
    return output;
  }

  static getAdjustedTraj(traj: ITrajectory, ms: IMachineState): ITrajectory {
    // * Adjusts the release position of the traj to match that of the machine state
    // * in x, y and z
    // * This perserves breaks, plate location, and vy from the given traj

    const endY = PLATE_EDGE_FT;

    const time = this.getFlightTimeSec(traj, endY);
    const breaks = this.getBreaks(traj);
    const platePos = this.getPlateLoc(traj, endY);

    const vna = BreakHelper.getVeloAccel({
      initial: {
        pos: {
          px: ms.px,
          py: ms.py,
          pz: ms.pz,
        },
        velo: {
          vx: traj.vx,
          vy: traj.vy,
          vz: traj.vz,
        },
      },
      final: {
        pos: {
          px: platePos.plate_x,
          py: endY,
          pz: platePos.plate_z,
        },
        time: time,
      },
      breaks: breaks,
    });

    const output: ITrajectory = {
      px: ms.px,
      py: ms.py,
      pz: ms.pz,

      vx: vna.velo.vx,
      vy: vna.velo.vy,
      vz: vna.velo.vz,

      ax: vna.accel.ax,
      ay: vna.accel.ay,
      az: vna.accel.az,

      wnet: traj.wnet,
    };

    return output;
  }

  static getLocalAccelWithoutGravity(traj: ITrajectory): IAcceleration {
    const xSq = Math.pow(traj.vx, 2);
    const ySq = Math.pow(traj.vy, 2);
    const zSq = Math.pow(traj.vz, 2);

    const invRot: IAziAltCoordinate = {
      azimuth_rad: -Math.asin(traj.vx / Math.sqrt(xSq + ySq)),
      altitude_rad: -Math.asin(traj.vz / Math.sqrt(xSq + ySq + zSq)),
    };

    const mx = getRotationMatrix({
      z_rad: invRot.azimuth_rad,
      x_rad: -invRot.altitude_rad,
    });

    const localAccel = new Vector3(
      traj.ax,
      traj.ay,
      traj.az + GRAVITY_FT_PER_SEC // cancel any amount of gravity baked into original az
    ).applyMatrix3(mx);

    return {
      ax: localAccel.x,
      ay: localAccel.y,
      az: localAccel.z,
    };
  }
}
